import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { makeStyles } from "@mui/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  FormControl,
  InputBase,
  Box,
  IconButton,
  Popper,
} from "@mui/material";
import api from "../api/config";
import { DeletePatient, GetAllPatient, SearchPatient } from "../api/Patient";
import { useSearch } from "../context/SearchContext";
import { CalendarMonth, Close } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import EditPatientModal from "./shared/EditPatientModal";
import ConfirmDeleteModal from "./shared/ConfirmDeleteModal";
import NewPatientModal from "./shared/NewPatientModal";
import SearchIcon from "@mui/icons-material/Search";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { ActionButton } from "./shared/action-button";
import AddIcon from '@mui/icons-material/Add';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #141B25;
  color: #fbfeff;
  overflow-y: hidden;
  &.noPadding {
    padding: 0 !important;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  background-color: transparent !important;
  box-shadow: none !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 240px);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1f2a36;
    border-radius: 100px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border: 1px solid #3e526a;
    border-radius: 100px;
  }

  .MuiTablePagination-root {
    background-color: #141B25 !important;
    color: #fbfeff !important;
    height: 26px;
  }

  .loadingContainer {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  }
`;

const StyledPaper = styled(Paper)`
  color: #fbfeff !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledTableRow = styled(TableRow)`
  background-color: #000;

  &:nth-of-type(even) {
    background-color: #141B25;
  }

  &:hover td {
    background-color: #3b4252 !important;
  }

  &.selected-row td {
    background-color: #0088cf !important;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const useStyles = makeStyles({
  headerCell: {
    background: "#365273",
    color: "#F8F8F8 !important",
    textTransform: "capitalize",
    fontWeight: 700,
    fontSize: "12px",
  },
  tableRow: {
    "& .MuiTableCell-head": {
      color: "#F8F8F8 !important",
      textTransform: "capitalize",
      fontWeight: 700,
      fontSize: "12px",
    },
  },
});

const columns = [
  { id: "client_patient_uuid", label: "Patient ID" },
  { id: "last_name", label: "Last Name" },
  { id: "first_name", label: "First Name" },
  { id: "date_of_birth", label: "DOB" },
  { id: "gender", label: "Gender" },
];

const AllPatientsTable = ({
  onSelectPatient,
  onToggleView,
  selectedPatient,
  setActiveTab,
  setIframeUrl,
}) => {
  const classes = useStyles();
  const { searchTerm, setSearchTerm } = useSearch();
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [showTable, setShowTable] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(columns);
  const [maxWidth, setMaxWidth] = useState(100);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [apptDate, setApptDate] = useState(dayjs().format("YYYYMMDD"));
  const containerRef = useRef(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const fetchControllerRef = useRef(null);
  const searchControllerRef = useRef(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [contextMenuPatient, setContextMenuPatient] = useState(null);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleNewPatientModalOpen = () => setIsNewPatientModalOpen(true);
  const handleNewPatientModalClose = () => setIsNewPatientModalOpen(false);
  const boxRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const popperRef = useRef(null);

  const handleCalendarDateChange = (date) => {
    if (date) {
      const formattedDate = date.format("YYYYMMDD");
      setApptDate(formattedDate);
    } else {
      setApptDate("");
    }
    setSelectedDate(date);
  };

  const updateVisibleColumns = useCallback(() => {
    setVisibleColumns(columns);
  }, []);

  const fetchPatients = useCallback(async () => {
    if (searchControllerRef.current) searchControllerRef.current.abort();
    if (fetchControllerRef.current) fetchControllerRef.current.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(apptDate && { appt_date: apptDate }),
      };
      const response = await api.get(`${GetAllPatient}${clientUuid}`, {
        params,
        signal: fetchControllerRef.current.signal,
      });
      setPatients(response.data.patients.reverse());
      setTotalRows(response.data.total_patients);
      setLoading(false);
    } catch (error) {
      if (error.name !== "CanceledError") {
        setLoading(false);
      }
    }
  }, [rowsPerPage, page, apptDate]);

  const searchPatients = useCallback(async () => {
    if (fetchControllerRef.current) fetchControllerRef.current.abort();
    if (searchControllerRef.current) searchControllerRef.current.abort();
    searchControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");
      const url = `${SearchPatient}?name=${searchTerm}&clientUID=${clientUuid}&limit=${rowsPerPage}&offset=${page * rowsPerPage
        }`;
      const response = await api.get(url, {
        signal: searchControllerRef.current.signal,
      });
      setPatients(response.data.patients.reverse());
      setTotalRows(response.data.total_patients);
      setLoading(false);
    } catch (error) {
      if (error.name !== "CanceledError") {
        setLoading(false);
      }
    }
  }, [searchTerm, rowsPerPage, page]);

  useEffect(() => {
    const observer = new ResizeObserver(updateVisibleColumns);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [updateVisibleColumns]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm && searchTerm.length >= 3) {
        fetchControllerRef.current?.abort();
        searchPatients();
      } else if (!searchTerm) {
        searchControllerRef.current?.abort();
        fetchPatients();
      }
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, fetchPatients, searchPatients]);

  useEffect(() => {
    if (!searchTerm) fetchPatients();
  }, [page, rowsPerPage, apptDate]);

  const onRefresh = () => {
    fetchPatients();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (patient) => {
    setSelectedPatientId(patient.uuid);
    onSelectPatient(patient);
    if (setActiveTab) {
      setActiveTab("patients");
    }
  };

  const handleDeletePatient = async () => {
    try {
      if (!contextMenuPatient) return;
      setDeleting(true);
      await api.post(`${DeletePatient}/${contextMenuPatient.uuid}`);
      alert("Record deleted successfully.");
      fetchPatients();
    } catch (error) {
      console.error("Error deleting patient:", error);
      alert("Failed to delete record.");
    } finally {
      setDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        boxRef.current &&
        !boxRef.current.contains(event.target) &&
        (!popperRef.current || !popperRef.current.contains(event.target))
      ) {
        setCalendarOpen(false);
        setAnchorEl(null);
      }
    };

    if (calendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calendarOpen, anchorEl]);

  return (
    <Container ref={containerRef} className={showTable ? "noPadding" : ""}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "12px",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FormControl>
                <Box
                  ref={boxRef}
                  onClick={(e) => {
                    setAnchorEl((prev) =>
                      calendarOpen ? null : boxRef.current
                    );
                    setCalendarOpen((prev) => !prev);
                  }}
                  sx={{
                    width: "210px",
                    height: "28px",
                    border: "1px solid #A1A5AC",
                    borderRadius: "4px",
                    padding: "6px 10px",
                    paddingLeft: "16px",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ flexGrow: 1 }}>
                    {selectedDate
                      ? dayjs(selectedDate).isSame(dayjs(), "day")
                        ? `${dayjs(selectedDate).format("MM/DD/YYYY")} (Today)`
                        : dayjs(selectedDate).format("MM/DD/YYYY")
                      : "Appointment Date"}
                  </Typography>
                  {selectedDate && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCalendarDateChange(null);
                      }}
                      size="small"
                      sx={{ color: "white", padding: 0, marginRight: "10px" }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                  <CalendarMonth sx={{ color: "white" }} />
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Popper
                    open={calendarOpen}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    sx={{
                      zIndex: 9999,
                      width: "200px",
                      marginTop: "8px",
                    }}
                  >
                    <Box ref={popperRef} sx={{ zIndex: 99999 }}>
                      <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={selectedDate}
                        onChange={(newValue) => {
                          setCalendarOpen(false);
                          handleCalendarDateChange(newValue);
                        }}
                        slotProps={{
                          layout: {
                            sx: {
                              color: "#fff",
                              backgroundColor: "#3f3f3f",
                            },
                          },
                        }}
                        sx={{
                          "& .MuiPickersLayout-root": {
                            backgroundColor: "#3f3f3f !important",
                          },
                          "& .MuiInputLabel-root": {
                            color: "#a5a5a5",
                            pointerEvents: "none",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#ffffff",
                            borderColor: "#F0F5F7",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "12px",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& > fieldset": { borderColor: "#ffffff" },
                            "&:hover > fieldset": { borderColor: "#ffffff" },
                            "&.Mui-focused > fieldset": {
                              borderColor: "#F0F5F7",
                            },
                          },
                          "& .MuiSvgIcon-root": {
                            fill: "white !important",
                          },
                          "& .MuiPaper-root": {
                            backgroundColor: "#000",
                          },
                          "& .MuiInputBase-root": {
                            width: "200px",
                            minWidth: "200px",
                          },
                          "& .MuiDialogActions-root .MuiButtonBase-root": {
                            color: "#ffffff",
                            border: "1px solid #ffffff",
                            borderRadius: "100px",
                          },
                          "& .MuiDayCalendar-weekDayLabel": {
                            color: "#ffffff",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Box>
                  </Popper>
                </LocalizationProvider>
              </FormControl>
              <ActionButton
                onClick={() => {
                  handleCalendarDateChange(dayjs());
                }}
                variantType="outlined"
              >
                Today
              </ActionButton>
            </div>
          </div>
          <ActionButton
            variantType="outlined"
            onClick={handleNewPatientModalOpen}
            icon={<AddIcon />}
          >
            New Patient
          </ActionButton>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              border: "1px solid #A1A5AC",
              height: "40px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              paddingLeft: "16px",
              flex: "1 1 360px",
              maxWidth: "360px",
            }}
          >
            <SearchIcon style={{ color: "white", marginRight: "12px" }} />
            <InputBase
              placeholder="Search Full Name, ID, DOB..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                color: "white",
                width: "100%",
                padding: "0",
              }}
            />
            {searchTerm && (
              <IconButton
                onClick={() => setSearchTerm("")}
                size="small"
                style={{
                  position: "absolute",
                  right: "12px",
                  color: "white",
                  padding: 0,
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </div>

          {/* <Button
            onClick={onRefresh}
            style={{
              color: "#0088CF",
              justifyContent: "flex-end",
              paddingRight: "0px",
            }}
          >
            <Refresh style={{ fill: "white", fontSize: "30px" }} />
          </Button> */}
        </div>
      </div>
      {showTable && (
        <>
          <StyledTableContainer component={StyledPaper}>
            {loading ? (
              <div className="loadingContainer">
                <CircularProgress style={{ color: "#FBFEFF" }} />
              </div>
            ) : (
              <TableWrapper>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow className={classes.tableRow}>
                      {visibleColumns.map((column, index) => (
                        <TableCell
                          key={column.id}
                          className={classes.headerCell}
                          sx={{
                            maxWidth: `${maxWidth}px`,
                            fontSize: "12px",
                            letterSpacing: "1%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            padding: "8px 16px",
                            verticalAlign: "middle",
                            borderBottom: "none",
                            height: "17px",
                            lineHeight: "140%",
                            textAlign: "left",
                            borderRight: index < visibleColumns.length - 1 ? "2px solid #141B25" : "none",
                            background: "#365273",
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patients.map((patient) => (
                      <StyledTableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={patient.uuid}
                        onClick={() => handleRowClick(patient)}
                        // onContextMenu={(e) => {
                        //   e.preventDefault();
                        //   setSelectedPatientId(patient.uuid);
                        //   setContextMenuAnchor(e.currentTarget);
                        //   setContextMenuPatient(patient);
                        // }}
                        className={
                          selectedPatientId === patient.uuid
                            ? "selected-row"
                            : ""
                        }
                      >
                        {visibleColumns.map((column) => (
                          <TableCell
                            key={column.id}
                            sx={{
                              maxWidth: `${maxWidth}px`,
                              fontSize: "12px",
                              lineHeight: "140%",
                              letterSpacing: "1%",
                              fontWeight: "400",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              padding: "8px 16px",
                              verticalAlign: "middle",
                              textTransform: "uppercase",
                              borderBottom: "1px solid #9DA2A7",
                              textAlign: "left",
                            }}
                          >
                            {column.id === "date_of_birth"
                              ? dayjs(patient[column.id]).format("MM/DD/YYYY")
                              : patient[column.id]}{" "}
                          </TableCell>
                        ))}
                      </StyledTableRow>
                    ))}
                    {patients.length === 0 && (
                      <TableRow style={{ height: "100%" }}>
                        <TableCell
                          colSpan={columns.length}
                          style={{ textAlign: "center", height: "100%" }}
                        >
                          No Data Available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableWrapper>
            )}
          </StyledTableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root": {
                minHeight: "26px !important",
                height: "26px !important",
                marginTop: "8px",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px !important",
              },
              ".MuiTablePagination-spacer": {
                display: "none",
              },
              ".MuiTablePagination-select": {
                color: "#F8F8F8",
              },
              ".MuiTablePagination-selectIcon": {
                color: "#F8F8F8",
              },
              ".MuiTablePagination-selectLabel": {
                margin: "0px",
              },
              ".MuiTablePagination-displayedRows": {
                margin: "0px",
              },
              ".MuiTablePagination-menuItem": {
                color: "#F8F8F8",
                "&:hover": {
                  backgroundColor: "#3b4252",
                },
                "&.Mui-selected": {
                  backgroundColor: "#3b4252",
                },
              },
              ".MuiTablePagination-actions .MuiButtonBase-root": {
                padding: "0px",
                color: "#F8F8F8",
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.2)"
                },
              },
              overflowY: "hidden",
            }}
            slotProps={{
              select: {
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#141b25",
                      color: "#FBFEFF",
                    },
                  },
                },
              },
            }}
          />
          {/* <div
            style={{
              display: "flex",
              justifyContent: "center",
              borderTop: "1px solid #3E526A",
              padding: "10px",
            }}
          >
            <Typography variant="body2" style={{ color: "#9DA2A7" }}>
              Powered by Nexus-MD @v{packageJson.version}
            </Typography>
          </div> */}
        </>
      )}
      {/* <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={() => setContextMenuAnchor(null)}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#000",
            color: "#FBFEFF",
          },
          "& .MuiMenuItem-root": {
            "&:hover": {
              backgroundColor: "#3b4252",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            console.log("Edit", contextMenuPatient);
            setContextMenuAnchor(null);
            setIsEditPatientModalOpen(true);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            console.log("Delete", contextMenuPatient);
            setContextMenuAnchor(null);
            setIsDeleteModalOpen(true);
          }}
        >
          Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            console.log("Move Images", contextMenuPatient);
            setContextMenuAnchor(null);
          }}
        >
          Move Images
        </MenuItem>
      </Menu> */}
      <EditPatientModal
        open={isEditPatientModalOpen}
        onClose={() => {
          setIsEditPatientModalOpen(false);
          fetchPatients();
        }}
        patient={contextMenuPatient}
      />
      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => handleDeletePatient()}
        loading={deleting}
      />
      <NewPatientModal
        open={isNewPatientModalOpen}
        onClose={handleNewPatientModalClose}
      />
    </Container>
  );
};

export default AllPatientsTable;
